import React, { useState } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { selectUser } from '../../common/auth/UserSlice';
import { useSelector } from 'react-redux';
import { handleUserUpdate } from '../../common/auth/AuthService';
import {
  Stack,
  Typography,
  CircularProgress,
  TextField,
  Box,
  Button,
} from '@mui/material';
import { showSuccessSnackbar } from '../../common/snackbar/SnackbarActions';
import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import WarnIcon from '@mui/icons-material/WarningAmber';

const validationSchema = Yup.object().shape({
  firstName: Yup.string().required('Required'),
  lastName: Yup.string().required('Required'),
  email: Yup.string().email('Invalid Email').required('Required'),
});

export default function UpdateUserForm({ callback = () => {} }) {
  const [errorMessage, setErrorMessage] = useState('');
  const userInfo = useSelector(selectUser);

  return (
    <Formik
      initialValues={{
        firstName: userInfo.firstName,
        lastName: userInfo.lastName,
        email: userInfo.email,
      }}
      validationSchema={validationSchema}
      onSubmit={async (values, { setSubmitting }) => {
        await handleUserUpdate(values)
          .then(() => {
            showSuccessSnackbar('Profile details updated');
            setSubmitting(false);
            callback();
          })
          .catch((err) => {
            console.error(err);
            setErrorMessage(err.message);
            setSubmitting(false);
          });
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        isSubmitting,
      }) => {
        return (
          <Form>
            <Stack alignItems='center' justifyContent='center'>
              <TextField
                name='firstName'
                type='text'
                label='First Name'
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.firstName}
                placeholder='First Name'
                variant='standard'
                error={errors.firstName && touched.firstName}
                helperText={touched.firstName && errors.firstName}
                sx={{ m: 1, width: '20vw', minWidth: '35ch' }}
                inputProps={{ maxLength: 64 }}
              />
              <TextField
                name='lastName'
                type='text'
                label='Last Name'
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.lastName}
                placeholder='Last Name'
                variant='standard'
                error={errors.lastName && touched.lastName}
                helperText={touched.lastName && errors.lastName}
                sx={{ m: 1, width: '20vw', minWidth: '35ch' }}
                inputProps={{ maxLength: 64 }}
              />
              <TextField
                name='email'
                type='email'
                label='Email'
                onChange={handleChange}
                onBlur={handleBlur}
                value={values.email}
                placeholder='Email'
                variant='standard'
                error={errors.email && touched.email}
                helperText={touched.email && errors.email}
                sx={{ m: 1, width: '20vw', minWidth: '35ch' }}
                inputProps={{ maxLength: 256 }}
              />
              <Typography sx={{ textAlign: 'left', paddingLeft: '14px' }}>
                <WarnIcon
                  sx={{ color: 'warning.main' }}
                  style={{ verticalAlign: 'middle' }}
                />
                NOTE: Changing your email address also changes your username
                that is used to log in. If you update your email address you
                will need to confirm your new email before being able to do
                anything else on the website.
              </Typography>
              {errorMessage && (
                <Typography sx={{ color: 'error.main' }}>
                  {errorMessage}
                </Typography>
              )}
              <Box sx={{ m: 1, position: 'relative' }}>
                <Button
                  type={'submit'}
                  disabled={isSubmitting}
                  variant='contained'
                >
                  <SaveIcon style={{ marginRight: '5' }} />
                  Save
                </Button>
                {isSubmitting && (
                  <CircularProgress
                    size={24}
                    sx={{
                      color: 'secondary.main',
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      marginTop: '-9px',
                      marginLeft: '-12px',
                    }}
                  />
                )}
              </Box>
              <Button
                type={'reset'}
                color='secondary'
                disabled={isSubmitting}
                variant='contained'
                onClick={callback}
              >
                <CancelIcon style={{ marginRight: '5' }} />
                Cancel
              </Button>
            </Stack>
          </Form>
        );
      }}
    </Formik>
  );
}
